import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { PhotoPreviewProvider } from "@/components/PhotoPreview";

// Comprehensive ResizeObserver error suppression
if (typeof window !== 'undefined') {
  // Suppress error events
  window.addEventListener('error', (e) => {
    if (e.message && e.message.includes('ResizeObserver')) {
      e.stopImmediatePropagation();
      e.preventDefault();
      return true;
    }
  }, true); // Use capture phase
  
  // Suppress unhandledrejection
  window.addEventListener('unhandledrejection', (e) => {
    if (e.reason && e.reason.message && e.reason.message.includes('ResizeObserver')) {
      e.stopImmediatePropagation();
      e.preventDefault();
      return true;
    }
  });
  
  // Suppress console errors
  const resizeObserverErr = console.error;
  console.error = (...args) => {
    const errorStr = args[0]?.toString?.() || '';
    if (errorStr.includes('ResizeObserver')) return;
    resizeObserverErr(...args);
  };
  
  // Suppress console warnings
  const resizeObserverWarn = console.warn;
  console.warn = (...args) => {
    const warnStr = args[0]?.toString?.() || '';
    if (warnStr.includes('ResizeObserver')) return;
    resizeObserverWarn(...args);
  };
}

// Pages
import LandingPage from "@/pages/LandingPage";
import RegisterPage from "@/pages/RegisterPage";
import LoginPage from "@/pages/LoginPage";
import HomePage from "@/pages/HomePage";
import AboutPage from "@/pages/AboutPage";
import AboutPageNew from "@/pages/AboutPageNew";
import GamesPage from "@/pages/GamesPage";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminGames from "@/pages/AdminGames";
import AdminUsers from "@/pages/AdminUsers";
import AdminAuditLogs from "@/pages/AdminAuditLogs";
import AdminAboutPage from "@/pages/AdminAboutPage";
import PendingApprovalPage from "@/pages/PendingApprovalPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import ProfilePage from "@/pages/ProfilePage";
import ContactPage from "@/pages/ContactPage";
import TermsPage from "@/pages/TermsPage";
import PrivacyPage from "@/pages/PrivacyPage";
import AdminLegalPage from "@/pages/AdminLegalPage";
import PlayerStatsPage from "@/pages/PlayerStatsPage";
import RatePlayersPage from "@/pages/RatePlayersPage";
import LeaderboardsPage from "@/pages/LeaderboardsPage";
import AdminRatings from "@/pages/AdminRatings";
import SettingsPage from "@/pages/SettingsPage";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  timeout: 30000, // 30 second timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token refresh helper
let isRefreshing = false;
let refreshPromise = null;

const refreshToken = async () => {
  if (isRefreshing) {
    return refreshPromise;
  }

  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return null;

      const response = await axios.post(`${API}/auth/refresh`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const newToken = response.data.access_token;
      localStorage.setItem("token", newToken);
      console.log("🔄 Token refreshed successfully");
      return newToken;
    } catch (error) {
      console.error("❌ Token refresh failed:", error);
      return null;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors with smart retry and network failures
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Handle network errors (timeout, connection refused, 520, etc.)
    if (!error.response || error.response?.status >= 500) {
      // Retry logic for network/server errors
      if (!originalRequest._retryCount) {
        originalRequest._retryCount = 0;
      }
      
      if (originalRequest._retryCount < 2) {
        originalRequest._retryCount++;
        console.log(`Retrying request (${originalRequest._retryCount}/2)...`);
        
        // Wait before retrying (exponential backoff)
        await new Promise(resolve => setTimeout(resolve, 1000 * originalRequest._retryCount));
        return axios(originalRequest);
      }
    }
    
    // If 401 and we haven't tried to refresh yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Auth endpoints (login/register/refresh) handle their own 401s.
      // Do NOT trigger a token refresh or a global redirect for them — a 401 here
      // simply means bad credentials and must surface to the calling page.
      const reqUrl = originalRequest?.url || '';
      const isAuthEndpoint =
        reqUrl.includes('/auth/login') ||
        reqUrl.includes('/auth/register') ||
        reqUrl.includes('/auth/refresh');
      if (isAuthEndpoint) {
        return Promise.reject(error);
      }

      originalRequest._retry = true;
      
      const newToken = await refreshToken();
      if (newToken) {
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return axios(originalRequest);
      }
      
      // Token refresh failed - logout user
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.href = "/login";
    }
    
    return Promise.reject(error);
  }
);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ratingHistory, setRatingHistory] = useState([]);

  useEffect(() => {
    const loadUser = async () => {
      const token = localStorage.getItem("token");
      if (token) {
        try {
          const response = await api.get("/auth/me");
          setUser(response.data);
        } catch (error) {
          console.error("Failed to load user:", error);
          // Don't remove token on network errors, only on auth errors (handled by interceptor)
          if (error.response?.status === 401) {
            localStorage.removeItem("token");
            localStorage.removeItem("user");
          }
        }
      }
      setLoading(false);
    };
    loadUser();

    // Set up automatic token refresh every 12 days (before 14-day expiry)
    const refreshInterval = setInterval(async () => {
      const token = localStorage.getItem("token");
      if (token) {
        console.log("🔄 Automatic token refresh triggered");
        await refreshToken();
      }
    }, 12 * 24 * 60 * 60 * 1000); // 12 days in milliseconds

    return () => clearInterval(refreshInterval);
  }, []);

  const login = (token, userData) => {
    // Set in-memory user FIRST so navigation/auth state always completes,
    // even if localStorage writes fail.
    setUser(userData);
    try {
      localStorage.setItem("token", token);
      // NOTE: we intentionally do NOT persist the full user object. It can contain a
      // large base64 profile photo that exceeds the localStorage quota (QuotaExceededError),
      // which previously broke login for users with big photos (worse in Incognito).
      // The user is rehydrated from the token via /auth/me on reload, so caching it is unnecessary.
    } catch (e) {
      console.error("Failed to persist auth token to localStorage:", e);
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUser(null);
    toast.success("Logged out successfully");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 to-blue-50">
        <div className="text-2xl font-semibold text-gray-700">Loading...</div>
      </div>
    );
  }

  const ProtectedRoute = ({ children, adminOnly = false }) => {
    const location = useLocation();
    if (!user) {
      // Preserve the intended destination (e.g. an admin email deep-link) so the
      // admin lands back on it after logging in instead of the generic home screen.
      const next = encodeURIComponent(location.pathname + location.search);
      return <Navigate to={`/login?next=${next}`} />;
    }
    if (!user.is_approved) {
      return <Navigate to="/pending-approval" />;
    }
    if (adminOnly && !user.is_admin) {
      return <Navigate to="/home" />;
    }
    return children;
  };
  
  const PublicRoute = ({ children }) => {
    return children;
  };

  // Declarative post-login redirect. Honors an internal ?next= path (e.g. an admin
  // email deep-link) so admins land back on the intended page after authenticating,
  // instead of the generic home screen. Avoids the navigate()-vs-setState race.
  const LoginRoute = () => {
    const location = useLocation();
    if (user) {
      if (!user.is_approved) return <Navigate to="/pending-approval" />;
      const nextParam = new URLSearchParams(location.search).get('next');
      const decoded = nextParam ? decodeURIComponent(nextParam) : null;
      const safe = decoded && decoded.startsWith('/') && !decoded.startsWith('//') ? decoded : null;
      return <Navigate to={safe || '/home'} />;
    }
    return <LoginPage onLogin={login} />;
  };

  return (
    <div className="App">
      <Toaster position="top-right" richColors />
      <PhotoPreviewProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={user ? <Navigate to="/home" /> : <LandingPage />} />
          <Route path="/about-old" element={<PublicRoute><AboutPage user={user} onLogout={logout} /></PublicRoute>} />
          <Route path="/about" element={<PublicRoute><AboutPageNew user={user} onLogout={logout} /></PublicRoute>} />
          <Route path="/terms" element={<PublicRoute><TermsPage user={user} onLogout={logout} /></PublicRoute>} />
          <Route path="/privacy" element={<PublicRoute><PrivacyPage user={user} onLogout={logout} /></PublicRoute>} />
          <Route path="/register" element={user ? <Navigate to="/home" /> : <RegisterPage onRegister={login} />} />
          <Route path="/login" element={<LoginRoute />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/pending-approval" element={user ? <PendingApprovalPage user={user} onLogout={logout} /> : <Navigate to="/login" />} />
          <Route path="/contact" element={<ContactPage />} />
          
          <Route
            path="/home"
            element={
              <ProtectedRoute>
                <HomePage user={user} onLogout={logout} />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/games"
            element={
              <ProtectedRoute>
                <GamesPage user={user} onLogout={logout} />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <ProfilePage user={user} onLogout={logout} onUpdateUser={setUser} />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            }
          />

          
          <Route
            path="/my-stats"
            element={
              <ProtectedRoute>
                <PlayerStatsPage user={user} onLogout={logout} />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/leaderboards"
            element={
              <ProtectedRoute>
                <LeaderboardsPage user={user} onLogout={logout} />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/games/:gameId/rate"
            element={
              <ProtectedRoute>
                <RatePlayersPage user={user} onLogout={logout} />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin"
            element={
              <ProtectedRoute adminOnly>
                <AdminDashboard user={user} onLogout={logout} />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/games"
            element={
              <ProtectedRoute adminOnly>
                <AdminGames user={user} onLogout={logout} />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute adminOnly>
                <AdminUsers user={user} onLogout={logout} />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/audit-logs"
            element={
              <ProtectedRoute adminOnly>
                <AdminAuditLogs user={user} onLogout={logout} />
              </ProtectedRoute>
            }
          />

          
          <Route
            path="/admin/legal"
            element={
              <ProtectedRoute adminOnly>
                <AdminLegalPage user={user} onLogout={logout} />
              </ProtectedRoute>
            }
          />


          <Route
            path="/admin/ratings"
            element={
              <ProtectedRoute adminOnly>
                <AdminRatings user={user} onLogout={logout} />
              </ProtectedRoute>
            }
          />


          <Route
            path="/admin/about"
            element={
              <ProtectedRoute adminOnly>
                <AdminAboutPage user={user} onLogout={logout} />
              </ProtectedRoute>
            }
          />
        </Routes>
      </BrowserRouter>
      </PhotoPreviewProvider>
    </div>
  );
}

export default App;
